import { LIVESTREAM_SECONDS, LIVESTREAM_STATUS } from '../const/livestream'
import { VIDEO_TYPES } from '../const/video'
import { isTouchDevice } from '../utils/Devices'
import type { Summary } from '../types/summary'
import type { Level } from '../modules/HlsEngine'
import type { MediaPropertiesType } from '../utils/Media'

type StoreState = {
  isPlaying: boolean
  hasPlaybackStarted: boolean
  currentTime: number
  duration: number
  videoController: any | null
  containerElement: HTMLDivElement | null
  mouseOver: boolean
  videoWidth: number | null
  videoHeight: number | null
  playerWidth: number
  playerHeight: number
  isFullscreen: boolean
  isWebkitFullscreen: boolean
  videoType: (typeof VIDEO_TYPES)[keyof typeof VIDEO_TYPES] | null
  mediaDescriptor: MediaPropertiesType | null
  currentSummary: (Summary & { language: string }) | null
  wasPlayingWhenSummaryOpened: boolean
  settingsMenuOpen: boolean
  liveSyncPosition: number
  isMobileLayout: boolean
  errorMessage: string | null
  isUserActive: boolean
  userActiveForced: boolean
  qualityLevels: Level[]
  isPicureInPictureSupported: boolean
  isAirplaySupported: boolean
  isAirplaying: boolean
  isChromecastSupported: boolean
  isChromecasting: boolean
  chromecastDeviceName: string | null
  liveStatus: (typeof LIVESTREAM_STATUS)[keyof typeof LIVESTREAM_STATUS]
  dvrDuration: number
  isBuffering: boolean
  isEnded: boolean
}
const defaults: StoreState = {
  isPlaying: false,
  hasPlaybackStarted: false,
  currentTime: 0,
  duration: 0,
  videoController: null,
  containerElement: null,
  mouseOver: false,
  videoWidth: null,
  videoHeight: null,
  playerWidth: 640,
  playerHeight: 480,
  isFullscreen: false,
  isWebkitFullscreen: false,
  videoType: null,
  mediaDescriptor: null,
  currentSummary: null,
  wasPlayingWhenSummaryOpened: false,
  settingsMenuOpen: false,
  liveSyncPosition: 0,
  isMobileLayout: isTouchDevice(),
  errorMessage: null,
  isUserActive: false,
  userActiveForced: false,
  qualityLevels: [],
  isPicureInPictureSupported: false,
  isAirplaySupported: false,
  isAirplaying: false,
  isChromecastSupported: false,
  isChromecasting: false,
  chromecastDeviceName: null,
  liveStatus: LIVESTREAM_STATUS.LOADING,
  dvrDuration: 0,
  isBuffering: false,
  isEnded: false,
}

const state = $state({ ...defaults })
let userInactiveTimeout: number | null = $state(null)

// $effect(() => {
//   if (state.isPlaying) {
//     state.hasPlaybackStarted = true
//   }
// })

// $effect(() => {
//   if (componentAttributesStore.state.forceMobileLayout) {
//     state.isMobileLayout = true
//   }
// })

const resetPlayer = () => {
  if (userInactiveTimeout) {
    clearTimeout(userInactiveTimeout)
    userInactiveTimeout = null
  }
  state.hasPlaybackStarted = false
  state.errorMessage = null
  state.isUserActive = false
  state.isPlaying = false
}

const forceUserActive = () => {
  state.userActiveForced = true
  state.isUserActive = true
}

const forceUserInactive = () => {
  if (userInactiveTimeout) {
    clearTimeout(userInactiveTimeout)
    userInactiveTimeout = null
  }
  state.isUserActive = false
  state.userActiveForced = false
}

const mouseMove = () => {
  if (userInactiveTimeout) {
    clearTimeout(userInactiveTimeout)
    userInactiveTimeout = null
  } else {
    state.isUserActive = true
  }

  userInactiveTimeout = setTimeout(() => {
    state.isUserActive = false
    userInactiveTimeout = null
  }, 3000)
}

const forceMobileLayout = (force: boolean) => {
  state.isMobileLayout = force ? true : defaults.isMobileLayout
}

const setCurrentSummary = (
  currentSummary: (Summary & { language: string }) | null,
  isPlaying?: boolean,
) => {
  state.currentSummary = currentSummary
  if (isPlaying !== undefined) {
    state.wasPlayingWhenSummaryOpened = isPlaying
  }
}

const isLiveStream = () => state.videoType === VIDEO_TYPES.LIVE

const isLive = () =>
  (state.liveSyncPosition || 0) - state.currentTime <
  LIVESTREAM_SECONDS.ASSIMILATED_LIVE

const showControls = () =>
  state.isUserActive || state.userActiveForced || state.isChromecasting

export default {
  state,
  defaults,
  resetPlayer,
  forceUserActive,
  forceUserInactive,
  mouseMove,
  setCurrentSummary,
  forceMobileLayout,
  isLiveStream,
  isLive,
  showControls,
}
